import {
  type AdminTenantRow as TenantRow,
} from "@nexys/user-management-client";
import {
  AlertCircle,
  ArrowUpDown,
  Building2,
  ListFilter,
  RefreshCcw,
  Search,
} from "lucide-react";
import { useMemo, useState } from "react";

import { useCall } from "../../hooks/use-call.js";
import { errorMessage } from "../../lib/error-message.js";
import { formatRelative } from "../../lib/utils.js";
import { EmptyState } from "../primitives/empty-state.js";
import { Alert, AlertDescription } from "../ui/alert.js";
import { Badge } from "../ui/badge.js";
import { Button } from "../ui/button.js";
import { Input } from "../ui/input.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select.js";
import { Skeleton } from "../ui/skeleton.js";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table.js";
import { TenantCreateDialog } from "./tenant-create-dialog.js";

const SORT_OPTIONS: {
  value: string;
  label: string;
  compare: (a: TenantRow, b: TenantRow) => number;
}[] = [
  {
    value: "name-asc",
    label: "Name A–Z",
    compare: (a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }),
  },
  {
    value: "name-desc",
    label: "Name Z–A",
    compare: (a, b) => b.name.localeCompare(a.name, undefined, { sensitivity: "base" }),
  },
  { value: "created-desc", label: "Newest first", compare: (a, b) => b.createdAt.localeCompare(a.createdAt) },
  { value: "created-asc", label: "Oldest first", compare: (a, b) => a.createdAt.localeCompare(b.createdAt) },
  { value: "members-desc", label: "Most members", compare: (a, b) => b.memberCount - a.memberCount },
];

export const TenantsTable = ({
  allowCreate = true,
  onSelect,
}: {
  allowCreate?: boolean;
  onSelect?: (id: string) => void;
}) => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("name-asc");
  const [productFilter, setProductFilter] = useState("all");
  const { data, error, loading, refresh } = useCall("adminListTenants");
  // Resolve tenant.projectId → product name. Superadmins (the only
  // callers of this table) see every project here.
  const projects = useCall("listProjects");
  const productNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects.data?.projects ?? []) map.set(p.id, p.name);
    return map;
  }, [projects.data]);

  const filtered = useMemo(() => {
    let list = data?.tenants ?? [];
    if (productFilter !== "all") {
      list = list.filter((t) =>
        productFilter === "none" ? !t.projectId : t.projectId === productFilter
      );
    }
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (t) =>
          t.name.toLowerCase().includes(q) ||
          t.slug.toLowerCase().includes(q) ||
          t.id.toLowerCase().includes(q)
      );
    }
    const compare = (SORT_OPTIONS.find((o) => o.value === sort) ?? SORT_OPTIONS[0]!)
      .compare;
    return [...list].sort(compare);
  }, [data, query, productFilter, sort]);

  const filterActive = productFilter !== "all";

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="relative min-w-[14rem] max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search tenants…"
            className="pl-9"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger
              className="h-8 w-[10.5rem]"
              aria-label="Filter by product"
            >
              <ListFilter className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All products</SelectItem>
              <SelectItem value="none">No product</SelectItem>
              {(projects.data?.projects ?? []).map((p) => (
                <SelectItem key={p.id} value={p.id}>
                  {p.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="h-8 w-[9.5rem]" aria-label="Sort by">
              <ArrowUpDown className="size-3.5 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORT_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={refresh}>
            <RefreshCcw />
            Refresh
          </Button>
          {allowCreate ? <TenantCreateDialog onCreated={refresh} /> : null}
        </div>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertCircle />
          <AlertDescription>{errorMessage(error)}</AlertDescription>
        </Alert>
      ) : null}

      <div className="overflow-hidden rounded-xl border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Tenant</TableHead>
              <TableHead>Slug</TableHead>
              <TableHead>Product</TableHead>
              <TableHead>Members</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-10" /></TableCell>
                  <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                </TableRow>
              ))
            ) : filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="p-0">
                  <EmptyState
                    icon={Building2}
                    title={
                      query || filterActive ? "No tenants match" : "No tenants yet"
                    }
                    description={
                      query || filterActive
                        ? "Try a different search or filter."
                        : "Create the first tenant to get started."
                    }
                    className="border-0 rounded-none bg-transparent"
                    action={
                      !query && !filterActive && allowCreate ? (
                        <TenantCreateDialog onCreated={refresh} />
                      ) : undefined
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((t) => (
                <TableRow
                  key={t.id}
                  className={onSelect ? "cursor-pointer" : undefined}
                  onClick={onSelect ? () => onSelect(t.id) : undefined}
                >
                  <TableCell>
                    <div className="flex items-center gap-2.5">
                      <div className="flex size-8 items-center justify-center rounded-md bg-muted text-xs font-semibold uppercase">
                        {t.name.slice(0, 2)}
                      </div>
                      <span className="font-medium">{t.name}</span>
                    </div>
                  </TableCell>
                  <TableCell className="font-mono text-xs">{t.slug}</TableCell>
                  <TableCell>
                    {t.projectId ? (
                      <Badge variant="muted">
                        {productNames.get(t.projectId) ?? t.projectId}
                      </Badge>
                    ) : (
                      <span className="text-muted-foreground text-xs">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="muted">{t.memberCount}</Badge>
                  </TableCell>
                  <TableCell title={t.createdAt} className="text-sm text-muted-foreground">
                    {formatRelative(t.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
